import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fxApi, FX_DEFAULT_INSTRUMENT } from "@/api/fxClient";
import { logix2Dock } from "@/api/adminOneClient";
import OandaFxTradingScreen from "@/components/oanda/OandaFxTradingScreen";
import OandaAccountCard from "@/components/oanda/OandaAccountCard";
import Logix2FxIntelligence from "@/components/oanda/Logix2FxIntelligence";

/**
 * CareLogix™ ORACLE — AdminOne OANDA Pro Trader sub-tab (Phase 22).
 *
 * Six sections per spec:
 *   1. Live OANDA Accounts (Demo + Live)
 *   2. LOGIX-2 FX Intelligence Board (top setups by confidence)
 *   3. Professional Trading Terminal (embedded OandaFxTradingScreen)
 *   4. LOGIX-2 Voice Trading Assistant (preset prompts)
 *   5. Global Market Intelligence (multi-asset snapshot)
 *   6. Live Intelligence Crawl (lightweight scrolling ticker)
 *
 * Strictly additive. Read-only/research. No automatic order execution.
 */

const VOICE_PROMPTS = [
  { prompt: "LOGIX-2 analyze GBP/USD", hint: "Direction + confidence + risk on the 5M timeframe." },
  { prompt: "LOGIX-2 what is the highest confidence trade?", hint: "Rank top pairs by Confidence Score." },
  { prompt: "LOGIX-2 show me EUR/USD",     hint: "Open EUR/USD chart in the trading terminal." },
  { prompt: "LOGIX-2 explain why this trade is bullish", hint: "Detail the drivers behind the current direction." },
  { prompt: "LOGIX-2 read the Africa daily brief", hint: "Pulls Africa watchtower daily brief out loud." },
  { prompt: "LOGIX-2 read the Global AI daily brief", hint: "Pulls Global AI watchtower daily brief out loud." },
];

const SECTIONS = [
  { id: "accounts",    label: "1 · Accounts" },
  { id: "intel",       label: "2 · LOGIX-2 Intelligence" },
  { id: "terminal",    label: "3 · Trading Terminal" },
  { id: "voice",       label: "4 · Voice Assistant" },
  { id: "global",      label: "5 · Global Markets" },
  { id: "crawl",       label: "6 · Live Crawl" },
];

export default function AdminOandaProTrader() {
  const [active, setActive] = useState("accounts");
  const [demoAccount, setDemoAccount] = useState(null);
  const [liveAccount, setLiveAccount] = useState(null);
  const [board, setBoard] = useState(null);
  const [boardTf, setBoardTf] = useState("5M");
  const [global, setGlobal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceMsg, setVoiceMsg] = useState("");

  // Loaders
  const loadAccounts = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: d1 }, { data: d2 }] = await Promise.all([
        fxApi.account("demo"),
        fxApi.account("live"),
      ]);
      setDemoAccount(d1);
      setLiveAccount(d2);
    } finally { setBusy(false); }
  }, []);

  const loadBoard = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await fxApi.intelligenceBoard(boardTf);
      setBoard(data);
    } finally { setBusy(false); }
  }, [boardTf]);

  const loadGlobal = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await fxApi.globalIntelligence();
      setGlobal(data);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { loadBoard(); }, [loadBoard]);
  useEffect(() => { loadGlobal(); }, [loadGlobal]);

  // Voice prompt — uses LOGIX-2 voice/speak endpoint with graceful browser fallback
  const speak = useCallback(async (text) => {
    setVoiceBusy(true);
    setVoiceMsg("");
    try {
      const { data } = await logix2Dock.voiceSpeak({ text });
      // If server returns an mp3 base64 (ElevenLabs configured), play it.
      if (data?.audio_base64) {
        const audio = new Audio(`data:audio/mpeg;base64,${data.audio_base64}`);
        audio.play().catch(() => { /* ignore */ });
        setVoiceMsg("Playing ElevenLabs audio…");
      } else if (typeof window !== "undefined" && "speechSynthesis" in window) {
        const utter = new window.SpeechSynthesisUtterance(text);
        utter.rate = 1.02;
        utter.pitch = 1.0;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utter);
        setVoiceMsg("Speaking via browser TTS…");
      } else {
        setVoiceMsg("Voice output unavailable in this browser.");
      }
    } catch (e) {
      setVoiceMsg(e?.response?.data?.detail || e?.message || "voice_failed");
    } finally {
      setVoiceBusy(false);
    }
  }, []);

  // Live crawl items
  const crawlItems = useMemo(() => {
    const items = ["LOGIX-2 monitoring 24/7"];
    if (board?.items?.length) {
      board.items.slice(0, 4).forEach((b) => {
        items.push(`${b.pair} ${String(b.direction).toUpperCase()} • Confidence ${b.confidence}% • Risk ${b.risk}%`);
      });
    }
    if (global?.categories) {
      global.categories.forEach((cat) => {
        const top = (cat.items || []).slice(0, 1)[0];
        if (top) items.push(`${cat.name}: ${top.label} ${top.change >= 0 ? "+" : ""}${top.change}${top.units}`);
      });
    }
    if (demoAccount) items.push(`DEMO NAV $${(demoAccount.nav || 0).toFixed(2)}`);
    return items;
  }, [board, global, demoAccount]);

  return (
    <div className="admin-fx" data-testid="admin-fx-pro-trader">
      <nav className="admin-fx__tabs" role="tablist" aria-label="Pro Trader sections">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={active === s.id}
            className={`admin-fx__tab ${active === s.id ? "is-active" : ""}`}
            onClick={() => setActive(s.id)}
            data-testid={`admin-fx-section-${s.id}`}
          >{s.label}</button>
        ))}
      </nav>

      {/* ───── Section 1 — Accounts ───── */}
      {active === "accounts" && (
        <section className="admin-fx__section" data-testid="admin-fx-accounts">
          <header className="admin-fx__section-head">
            <h3 className="admin-fx__section-title">Live OANDA Accounts (Demo + Live)</h3>
            <button className="fx-btn" onClick={loadAccounts} disabled={busy} data-testid="admin-fx-accounts-refresh">↻ Refresh</button>
          </header>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))", gap: 12 }}>
            <OandaAccountCard account={demoAccount} onTrade={() => setActive("terminal")} onAddFunds={() => {}} onSignOut={() => {}} />
            <OandaAccountCard account={liveAccount} onTrade={() => setActive("terminal")} onAddFunds={() => {}} onSignOut={() => {}} />
          </div>
          {!liveAccount?.live_available && (
            <p className="fx-discl" style={{ marginTop: 8 }}>
              Live OANDA keys not configured. Demo balances are deterministic research data.
            </p>
          )}
        </section>
      )}

      {/* ───── Section 2 — LOGIX-2 FX Intelligence ───── */}
      {active === "intel" && (
        <section className="admin-fx__section" data-testid="admin-fx-intel">
          <header className="admin-fx__section-head">
            <h3 className="admin-fx__section-title">LOGIX-2 FX Intelligence Board</h3>
            <div style={{ display: "flex", gap: 6 }}>
              {["2M", "5M", "10M", "15M"].map((tf) => (
                <button
                  key={tf}
                  type="button"
                  className={`fx-btn ${boardTf === tf ? "fx-btn--primary" : ""}`}
                  onClick={() => setBoardTf(tf)}
                  data-testid={`admin-fx-board-tf-${tf}`}
                >{tf}</button>
              ))}
              <button className="fx-btn" onClick={loadBoard} disabled={busy} data-testid="admin-fx-board-refresh">↻</button>
            </div>
          </header>
          {!board?.items?.length && <p className="fx-discl">Loading intelligence board…</p>}
          {board?.items?.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 10 }}>
              {board.items.map((it) => (
                <div key={it.instrument} className="fx-intel" data-testid={`admin-fx-board-${it.instrument}`}>
                  <header className="fx-intel__head">
                    <span className="fx-intel__title">{it.pair} · {it.timeframe}</span>
                    <span className={`fx-intel__dir-pill ${it.direction}`}>{it.direction}</span>
                  </header>
                  <div className="fx-intel__scores">
                    <div className="fx-intel__score">
                      <span className="fx-intel__score-label">Confidence</span>
                      <span className="fx-intel__score-val">{it.confidence}%</span>
                      <div className="fx-intel__bar"><div className="fx-intel__bar-fill" style={{ width: `${it.confidence}%` }} /></div>
                    </div>
                    <div className="fx-intel__score">
                      <span className="fx-intel__score-label">Risk</span>
                      <span className="fx-intel__score-val">{it.risk}%</span>
                      <div className="fx-intel__bar"><div className="fx-intel__bar-fill risk" style={{ width: `${it.risk}%` }} /></div>
                    </div>
                  </div>
                  <div className="fx-intel__targets">
                    <div className="fx-intel__target"><span className="fx-intel__target-label">Entry</span><span className={`fx-intel__target-val ${it.direction === "bullish" ? "is-buy" : "is-sell"}`}>{it.entry}</span></div>
                    <div className="fx-intel__target"><span className="fx-intel__target-label">Target</span><span className="fx-intel__target-val is-buy">{it.target}</span></div>
                    <div className="fx-intel__target"><span className="fx-intel__target-label">Stop</span><span className="fx-intel__target-val is-sell">{it.stop}</span></div>
                  </div>
                  <p className="fx-discl">R/R ≈ {it.reward_risk_ratio}x · spread {it.spread_pips} pips</p>
                </div>
              ))}
            </div>
          )}
          <p className="fx-discl" style={{ marginTop: 8 }}>{board?.disclaimer}</p>
        </section>
      )}

      {/* ───── Section 3 — Professional Trading Terminal ───── */}
      {active === "terminal" && (
        <section className="admin-fx__section" data-testid="admin-fx-terminal">
          <header className="admin-fx__section-head">
            <h3 className="admin-fx__section-title">Professional Trading Terminal</h3>
          </header>
          <OandaFxTradingScreen embedded />
        </section>
      )}

      {/* ───── Section 4 — Voice Assistant ───── */}
      {active === "voice" && (
        <section className="admin-fx__section" data-testid="admin-fx-voice">
          <header className="admin-fx__section-head">
            <h3 className="admin-fx__section-title">LOGIX-2 Voice Trading Assistant</h3>
            {voiceMsg && <span className="fx-discl">{voiceMsg}</span>}
          </header>
          <div className="fx-voice-grid">
            {VOICE_PROMPTS.map((vp, i) => (
              <div key={`vp-${i}`} className="fx-voice-card" data-testid={`admin-fx-voice-prompt-${i}`}>
                <p className="fx-voice-card__prompt">"{vp.prompt}"</p>
                <p className="fx-voice-card__hint">{vp.hint}</p>
                <div className="fx-voice-card__actions">
                  <button
                    type="button"
                    className="fx-btn fx-btn--primary"
                    onClick={() => speak(vp.prompt)}
                    disabled={voiceBusy}
                    data-testid={`admin-fx-voice-speak-${i}`}
                  >🔊 Read aloud</button>
                </div>
              </div>
            ))}
          </div>
          <p className="fx-discl" style={{ marginTop: 8 }}>
            LOGIX-2 voice uses ElevenLabs when configured, otherwise browser SpeechSynthesis.
            All prompts are research-only; LOGIX-2 never auto-executes trades.
          </p>
        </section>
      )}

      {/* ───── Section 5 — Global Markets ───── */}
      {active === "global" && (
        <section className="admin-fx__section" data-testid="admin-fx-global">
          <header className="admin-fx__section-head">
            <h3 className="admin-fx__section-title">Global Market Intelligence</h3>
            <button className="fx-btn" onClick={loadGlobal} disabled={busy} data-testid="admin-fx-global-refresh">↻ Refresh</button>
          </header>
          {!global?.categories?.length && <p className="fx-discl">Loading global market intelligence…</p>}
          {global?.categories?.length > 0 && (
            <div className="fx-gmi">
              {global.categories.map((cat) => (
                <div key={cat.name} className="fx-gmi__card" data-testid={`admin-fx-global-${cat.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <h4>{cat.name}</h4>
                  <ul className="fx-gmi__list">
                    {cat.items.map((it, i) => (
                      <li key={`gmi-${cat.name}-${i}`} className={`fx-gmi__row ${it.change > 0.1 ? "is-up" : (it.change < -0.1 ? "is-down" : "is-flat")}`}>
                        <span>{it.label}</span>
                        <span className="fx-gmi__delta">
                          {it.change >= 0 ? "+" : ""}{it.change}{it.units}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          <p className="fx-discl" style={{ marginTop: 8 }}>{global?.disclaimer}</p>
        </section>
      )}

      {/* ───── Section 6 — Live Crawl ───── */}
      {active === "crawl" && (
        <section className="admin-fx__section" data-testid="admin-fx-crawl">
          <header className="admin-fx__section-head">
            <h3 className="admin-fx__section-title">Live Intelligence Crawl</h3>
          </header>
          <div className="fx-crawl" aria-label="Live intelligence ticker">
            <span className="fx-crawl__label">● LIVE</span>
            <div className="fx-crawl__track">
              {[...crawlItems, ...crawlItems].map((line, i) => (
                <span key={`cr-${i}`} data-testid={i < crawlItems.length ? `admin-fx-crawl-item-${i}` : undefined}>
                  {line} <span className="fx-crawl__dot" aria-hidden="true">•</span>
                </span>
              ))}
            </div>
          </div>
          <p className="fx-discl" style={{ marginTop: 8 }}>
            Composite ticker of LOGIX-2 intelligence + global market deltas. Research only.
          </p>
        </section>
      )}
    </div>
  );
}
